import React from 'react';
import { Box, Typography } from '@mui/material';
import CustomModal from '../../../../../shared/CustomModal/CustomModal';
import CustomCard from '../../../../../shared/CustomCard/CustomCard';
import CustomButton from '../../../../../shared/CustomButton/CustomButton';
import zeplinColor from '../../../../../../theme/Color';

interface BlockConfirmationModalProps {
  open: boolean;
  userName?: string | null;
  userCount?: number;
  isBlocking: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const BlockConfirmationModal: React.FC<BlockConfirmationModalProps> = ({
  open,
  userName,
  userCount,
  isBlocking,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  return (
    <CustomModal handleClose={onCancel} open={open}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CustomCard
          styles={{
            borderRadius: '14px',
            padding: { xs: '20px', xl: '24px' },
            backgroundColor: zeplinColor.Neutral1,
            width: '375px',
            boxShadow: 'rgba(0, 0, 0, 0.15)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
              {isBlocking ? 'Block User' : 'Unblock User'}
            </Typography>

            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              {userCount && userCount > 1 ? (
                isBlocking ? (
                  <>
                    Are you sure you want to block{' '}
                    <Typography
                      variant='responsiveBody3'
                      component='span'
                      color={zeplinColor.BasicBlack}
                      fontWeight={600}
                    >
                      {userCount} users
                    </Typography>
                    ? This will restrict their access to the Huddle.
                  </>
                ) : (
                  <>
                    Are you sure you want to unblock{' '}
                    <Typography
                      variant='responsiveBody3'
                      component='span'
                      color={zeplinColor.BasicBlack}
                      fontWeight={600}
                    >
                      {userCount} users
                    </Typography>
                    ? They will regain access to their accounts.
                  </>
                )
              ) : isBlocking ? (
                <>
                  Are you sure you want to block{' '}
                  <Typography
                    variant='responsiveBody3'
                    component='span'
                    color={zeplinColor.BasicBlack}
                    fontWeight={600}
                  >
                    {userName}
                  </Typography>
                  ? This will restrict their access to the Huddle.
                </>
              ) : (
                <>
                  Are you sure you want to unblock{' '}
                  <Typography
                    variant='responsiveBody3'
                    component='span'
                    color={zeplinColor.BasicBlack}
                    fontWeight={600}
                  >
                    {userName}
                  </Typography>
                  ? They will regain access to their account.
                </>
              )}
            </Typography>

            <Box
              sx={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}
            >
              <CustomButton
                disabled={loading}
                onClick={onCancel}
                variant='outlined'
                color='primary'
                style={{
                  borderRadius: '8px',
                  height: '40px',
                  padding: '8px 16px',
                  flex: 1,
                  border: `1px solid ${zeplinColor.BasicBlack}`,
                  '&.hover': {
                    backgroundColor: zeplinColor.Neutral3,
                  },
                }}
                textStyle={{ color: zeplinColor.BasicBlack }}
                text='Cancel'
              />
              <CustomButton
                disabled={loading}
                onClick={onConfirm}
                variant='contained'
                color='primary'
                style={{
                  borderRadius: '8px',
                  height: '40px',
                  padding: '8px 16px',
                  backgroundColor: loading
                    ? zeplinColor.Neutral3
                    : isBlocking
                    ? zeplinColor.Error
                    : zeplinColor.Brand1,
                  flex: 1,
                }}
                textStyle={{ color: zeplinColor.Neutral1 }}
                text={
                  loading
                    ? isBlocking
                      ? 'Blocking...'
                      : 'Unblocking...'
                    : isBlocking
                    ? 'Block'
                    : 'Unblock'
                }
              />
            </Box>
          </Box>
        </CustomCard>
      </Box>
    </CustomModal>
  );
};

export default BlockConfirmationModal;
